import React, { useMemo, useState } from "react";
import PopupModules from "src/features/auth/components/PopupModules";
import { Button } from "src/business-types/grocery/components/Button";
import InputBox from "../../components/InputBox";
import { Order } from "src/shared/types";


type Props = {
  returnReplace: boolean;
  setReturnReplace: () => void;
  order: Order;
  orderChange?: Order;
  changeConfirm: (data: changeDetails) => void;
  osb: number;
};

export type changeDetails = {
  difference: number;
  paymentType: string;
  amount: number;
  balance: number;
  accountAdjesment: string;
};

function ReturnReplace({
  returnReplace,
  setReturnReplace,
  order,
  orderChange,
  changeConfirm,
  osb,
}: Props) {
  const [mode, setMode] = useState<"refund" | "credit">("refund");
  const [selectedOption, setSelectedOption] = useState<string>("Cash");
  const [resPrice, setResPrice] = useState<string>("");

  // ── New interface: finalAmount lives in orders[0] ─────────────────────────
  const oldPaid = Number(order?.orders?.[0]?.finalAmount ?? 0);
  const newPaid = Number(orderChange?.orders?.[0]?.finalAmount ?? 0);
  const difference = newPaid - oldPaid;

  // Walk-in check uses customerDetails.firstName
  const isWalkIn =
    (order?.customerDetails?.firstName ?? "").toUpperCase() === "XXXX";

  // ── New balance — operator precedence fixed with explicit grouping ─────────
  const newBalance = useMemo(() => {
    const entered = Number(resPrice || 0);
    return osb + difference - entered;
  }, [resPrice, difference, osb]);

  // ── Input handler ─────────────────────────────────────────────────────────
  const pricePayment = (value: string) => {
    if (!/^\d*(\.\d{0,2})?$/.test(value)) return;
    const maxAllowed = osb + difference;
    if (Number(value) <= maxAllowed + 0.001) setResPrice(value);
  };

  // For walk-in: must pay full difference; otherwise use entered amount
  const finalAmount =
    isWalkIn && difference > 0 ? Math.abs(difference) : Number(resPrice || 0);

  // Customer display name
  const customerName = [
    order?.customerDetails?.firstName,
    order?.customerDetails?.middleName,
    order?.customerDetails?.lastName,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <PopupModules
      isOpen={returnReplace}
      onClose={setReturnReplace}
      className="w-1/2 max-h-2/3"
      title="Order Modify Confirmation"
    >
      <div className="p-4 space-y-4">
        {/* Customer */}
        <div className="text-lg font-semibold">
          {isWalkIn ? "Walk-in Customer" : customerName}
        </div>

        {/* Summary */}
        <div className="bg-gray-100 p-3 rounded space-y-2">
          <div className="flex justify-between">
            <span>Old Payment</span>
            <span>{oldPaid.toFixed(2)}</span>
          </div>
          <div className="flex justify-between">
            <span>Modified Payment</span>
            <span>{newPaid.toFixed(2)}</span>
          </div>
          <div
            className={`flex justify-between font-bold ${difference < 0 ? "text-red-500" : "text-green-600"
              }`}
          >
            <span>Difference</span>
            <span>{Math.abs(difference).toFixed(2)}</span>
          </div>
        </div>

        {/* Extra Payment Required */}
        {difference > 0 && (
          <div className="bg-gray-100 p-3 rounded space-y-3">
            <div className="text-green-600 font-medium text-center">
              Extra payment required. Adjust below.
            </div>

            <div className="flex gap-3 flex-wrap justify-center">
              {["Buy on credit", "Card", "Cash"].map((t) => (
                <div
                  key={t}
                  className={`px-3 py-2 flex gap-2 items-center cursor-pointer border rounded-md ${selectedOption === t
                    ? "bg-blue-100 border-blue-500"
                    : "border-gray-300"
                    }`}
                  onClick={() => setSelectedOption(t)}
                >
                  <input
                    type="radio"
                    checked={selectedOption === t}
                    onChange={() => setSelectedOption(t)}
                  />
                  <span>{t}</span>
                </div>
              ))}
            </div>

            <div className="grid grid-cols-2 gap-y-2 items-center text-sm">
              <span className="font-medium">Difference</span>
              <span>{difference.toFixed(2)}</span>

              <span className="font-medium">Current OSB</span>
              <span>{osb.toFixed(2)}</span>

              <span className="font-medium">Enter Payment</span>
              <InputBox
                disabled={selectedOption === "Buy on credit" || isWalkIn}
                value={isWalkIn ? Math.abs(difference).toFixed(2) : resPrice}
                onChange={(e) => pricePayment(e.target.value)}
              />

              <span className="font-medium">New Balance</span>
              <span>{newBalance.toFixed(2)}</span>
            </div>
          </div>
        )}

        {/* Refund Required */}
        {difference < 0 && (
          <div className="space-y-3">
            <p className="font-medium text-red-500">Refund Required</p>
            {!isWalkIn ? (
              <div className="flex gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    checked={mode === "refund"}
                    onChange={() => setMode("refund")}
                  />
                  Refund (Cash)
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    checked={mode === "credit"}
                    onChange={() => setMode("credit")}
                  />
                  Add to Credit
                </label>
              </div>
            ) : (
              <p className="text-sm text-gray-500">
                Only cash refund available for walk-in customers.
              </p>
            )}
          </div>
        )}

        {/* No change */}
        {difference === 0 && (
          <p className="text-center text-gray-500 text-sm">
            No payment difference. Confirm to save changes.
          </p>
        )}

        {/* Actions */}
        <div className="flex justify-end gap-3 pt-4">
          <Button label="Cancel" variant="cancel" onClick={setReturnReplace} />
          <Button
            label="Confirm"
            onClick={() => {
              changeConfirm({
                difference,
                paymentType: selectedOption,
                amount: finalAmount,
                balance: newBalance,
                accountAdjesment: mode,
              });
              setReturnReplace();
            }}
          />
        </div>
      </div>
    </PopupModules>
  );
}

export default ReturnReplace;